import {
	faExclamationTriangle,
	faPencilAlt,
	faSpinner,
	faTrash
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { apiClient } from "@/api/axiosInstance";
import { API_ENDPOINTS, resolveEndpoint } from "@/api/endpoints";
import { useToast } from "@/components/common/Toast";
import SidePanel from "@/components/Sidepanel";
import { useAuthStore } from "@/store/auth.store";
import { useSystemRoleStore } from "@/store/systemRole.store";
import { ApiError, handleApiError } from "@/utils/errorHandler";
import AddRoleForm from "./AddRoleForm";

interface Role {
	id: number;
	name: string;
	permissions: number[];
}

export default function RolesPage() {
	const { brandId } = useAuthStore();
	const queryClient = useQueryClient();
	const { success } = useToast();

	const systemRolesApi = {
		getRoles: async () => {
			const response = await apiClient.get(
				resolveEndpoint(API_ENDPOINTS, ["BRAND", "ROLES", "LIST"], brandId)
			);
			return response.data;
		},

		createRole: async (data: { name: string; permissions: number[] }) => {
			const response = await apiClient.post(
				resolveEndpoint(API_ENDPOINTS, ["BRAND", "ROLES", "CREATE"], brandId),
				data
			);
			return response.data;
		},

		updateRole: async (
			id: number,
			data: { name: string; permissions: number[] }
		) => {
			const response = await apiClient.put(
				resolveEndpoint(
					API_ENDPOINTS,
					["BRAND", "ROLES", "UPDATE_BY_ID"],
					brandId,
					id
				),
				data
			);
			return response.data;
		},

		deleteRole: async (id: number) => {
			const response = await apiClient.delete(
				resolveEndpoint(
					API_ENDPOINTS,
					["BRAND", "ROLES", "DELETE_BY_ID"],
					brandId,
					id
				)
			);
			return response.data;
		}
	};

	const {
		systemRoleToDelete,
		systemRoleToEdit,
		isDeleteModalOpen,
		isEditModalOpen,
		setSystemRoleToDelete,
		setSystemRoleToEdit,
		setIsDeleteModalOpen,
		setIsEditModalOpen
	} = useSystemRoleStore();

	const [formData, setFormData] = useState({
		name: "",
		permissions: [] as number[]
	});

	const { data: roles = [], isLoading } = useQuery({
		queryKey: [`brand-roles-${brandId}`],
		queryFn: systemRolesApi.getRoles
	});

	const createMutation = useMutation({
		mutationFn: systemRolesApi.createRole,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [`brand-roles-${brandId}`] });
			success("Role created successfully");
			handleCloseModal();
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to create role");
		}
	});

	const updateMutation = useMutation({
		mutationFn: ({
			id,
			data
		}: {
			id: number;
			data: { name: string; permissions: number[] };
		}) => systemRolesApi.updateRole(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [`brand-roles-${brandId}`] });
			success("Role updated successfully");
			handleCloseModal();
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to update role");
		}
	});

	const deleteMutation = useMutation({
		mutationFn: systemRolesApi.deleteRole,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [`brand-roles-${brandId}`] });
			success("Role deleted successfully");
			setIsDeleteModalOpen(false);
			setSystemRoleToDelete(null);
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to delete role");
		}
	});

	const handleEditClick = (role: Role) => {
		setSystemRoleToEdit(role);
		setFormData({
			name: role.name,
			permissions: role.permissions
		});
		setIsEditModalOpen(true);
	};

	const handleDeleteClick = (role: Role) => {
		setSystemRoleToDelete(role);
		setIsDeleteModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsEditModalOpen(false);
		setSystemRoleToEdit(null);
		setFormData({ name: "", permissions: [] });
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (systemRoleToEdit) {
			updateMutation.mutate({
				id: systemRoleToEdit.id,
				data: formData
			});
		} else {
			createMutation.mutate(formData);
		}
	};

	if (isLoading) {
		return (
			<div className="flex justify-center items-center h-screen">
				<FontAwesomeIcon
					icon={faSpinner}
					spin
					className="h-8 w-8 text-blue-500"
				/>
			</div>
		);
	}

	return (
		<div className="flex-1">
			<div className="flex justify-end items-center mb-6">
				<button
					type="button"
					onClick={() => setIsEditModalOpen(true)}
					className="bg-blue-fynxt text-white px-4 py-2 text-sm rounded hover:bg-blue-700 transition-colors"
				>
					Create Role
				</button>
			</div>

			<div className="bg-white shadow-sm rounded-lg overflow-x-auto overflow-y-auto">
				<table className="min-w-full divide-y divide-gray-200 custom-table">
					<thead className="bg-gray-100 sticky z-10 top-0">
						<tr>
							<th
								scope="col"
								className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider w-2/5"
							>
								Name
							</th>
							<th
								scope="col"
								className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider w-2/5"
							>
								Permissions
							</th>
							<th
								scope="col"
								className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider w-1/5"
							>
								Actions
							</th>
						</tr>
					</thead>
					<tbody className="bg-white divide-y divide-gray-200">
						{roles.length ? (
							roles.map((role: Role) => (
								<tr key={role.id} className="hover:bg-gray-50">
									<td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
										{role.name}
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
										{role.permissions.length} permissions
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-center">
										<div className="flex justify-center space-x-2">
											<button
												type="button"
												onClick={() => handleEditClick(role)}
												className="text-green-600 hover:text-green-900 p-1"
												title="Edit Role"
											>
												<FontAwesomeIcon icon={faPencilAlt} />
											</button>
											<button
												type="button"
												onClick={() => handleDeleteClick(role)}
												className="text-red-600 hover:text-red-900 p-1"
												title="Delete Role"
											>
												<FontAwesomeIcon icon={faTrash} />
											</button>
										</div>
									</td>
								</tr>
							))
						) : (
							<tr>
								<td
									colSpan={3}
									className="px-6 py-6 text-center text-gray-400 text-sm whitespace-nowrap"
								>
									No roles data found.
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			<SidePanel
				isOpen={isEditModalOpen}
				onClose={handleCloseModal}
				title={systemRoleToEdit ? "Edit Role" : "Create Role"}
				closeOnOutsideClick={false}
			>
				<AddRoleForm
					formData={formData}
					isEditing={!!systemRoleToEdit}
					isSubmitting={createMutation.isPending || updateMutation.isPending}
					handleChange={(e) => {
						setFormData((prev) => ({
							...prev,
							[e.name]: e.value
						}));
					}}
					handleSubmit={handleSubmit}
				/>
			</SidePanel>

			{isDeleteModalOpen && systemRoleToDelete && (
				<div className="fixed inset-0 z-50 overflow-y-auto">
					<div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
						<div
							className="fixed inset-0 transition-opacity"
							aria-hidden="true"
						>
							<div className="absolute inset-0 bg-gray-500 opacity-75" />
						</div>

						<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
							<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
								<div className="sm:flex sm:items-start">
									<div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
										<FontAwesomeIcon
											icon={faExclamationTriangle}
											className="h-6 w-6 text-red-600"
										/>
									</div>
									<div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
										<h3 className="text-lg leading-6 font-medium text-gray-900">
											Delete Role
										</h3>
										<div className="mt-2">
											<p className="text-sm text-gray-500">
												Are you sure you want to delete{" "}
												{systemRoleToDelete.name}? This action cannot be undone.
											</p>
										</div>
									</div>
								</div>
							</div>
							<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
								<button
									type="button"
									className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
									onClick={() => deleteMutation.mutate(systemRoleToDelete.id)}
									disabled={deleteMutation.isPending}
								>
									{deleteMutation.isPending ? (
										<>
											<FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
											Deleting...
										</>
									) : (
										"Delete"
									)}
								</button>
								<button
									type="button"
									className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
									onClick={() => setIsDeleteModalOpen(false)}
								>
									Cancel
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
